<template>
    <TreeView
        v-model="selected"
        :items="localCustomers"
        :load-children="loadChildren"
        :leaf-level="2"
    />
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { STEP_ELEMENT_TYPE_ENUM } from '@/constants/cyclicRevision';
import { mapFields } from 'vuex-map-fields';
import TreeView from '@/components/treeView/TreeView.vue';

export default {
    name: 'LazyPointsTree',
    components: { TreeView },
    props: {
        customers: Array,
        loadedAllCustomers: Boolean
    },
    data: () => ({
        selected: [],
        localCustomers: []
    }),
    watch: {
        selected: {
            handler (val, oldVal) {
                if (JSON.stringify(val) === JSON.stringify(oldVal) && val.length !== 0) return; // when changing selected from outside, watcher is triggered twice - this is a workaround

                if (this.activeSteps) {
                    const uniqueActivePoints = new Set(this.activeSteps.filter(step => step.point.id).map(step => step.point.id));

                    if (val.length === uniqueActivePoints.size) {
                        this.$store.dispatch('cyclicRevisions/setAllActivePointsSelected', true);
                    }
                }

                this.dispatchPointsActions(val);
            }
        },
        customers: {
            handler (val) {
                this.localCustomers = val.map(item => ({
                    ...item,
                    id: `${item.id}_customer`,
                    children: []
                }));
            }
        },
        activeSteps: {
            handler (val) {
                if (val && val.length > 0) {
                    const localPoints = [];
                    const customersMap = new Map();
                    const sitesMap = new Map();
                    val.filter(step => step.point.id).forEach(point => {
                        if (!customersMap.has(point.point.clientId)) {
                            customersMap.set(point.point.clientId, {
                                id: point.point.clientId,
                                elementType: 'customer',
                                children: []
                            });
                        }

                        if (!sitesMap.has(point.point.locationId)) {
                            sitesMap.set(point.point.locationId, {
                                id: point.point.locationId,
                                name: point.point.locationName,
                                elementType: 'site',
                                children: []
                            });
                        }

                        const pointObject = {
                            id: point.point.id,
                            name: point.point.name,
                            elementType: 'point'
                        };

                        sitesMap
                            .get(point.point.locationId)
                            .children.push(pointObject);
                        localPoints.push({
                            ...pointObject,
                            parents: [point.point.locationId, `${point.point.clientId}_customer`]
                        });

                        customersMap
                            .get(point.point.clientId)
                            .children.push(
                                sitesMap.get(point.point.locationId)
                            );
                    });

                    this.selected = localPoints;
                }
            }
        }
    },
    computed: {
        ...mapGetters('customers', [
            'listingCustomers'
        ]),
        ...mapState('customers', [
            'sitesPerCustomers', 'locationsPerSite'
        ]),
        ...mapState('manageTrack', [
            'trackData'
        ]),
        ...mapFields('manageTrack', [
            'trackData.sequence.activeSteps'
        ])
    },
    methods: {
        updateActive (val) {
            this.selected = val.filter(item => !item.locked);
        },
        async loadChildren (item) {
            switch (item.elementType) {
            case 'customer':
                await this.loadSites(item);
                break;
            case 'site':
                await this.loadPoints(item);
                break;
            default:
            }
        },
        async loadSites (customer) {
            let i = -1;
            let break_loop = false;

            while (!break_loop) {
                const customerId = customer.id.split('_')[0];
                await this.$store.dispatch('customers/getSitesPerCustomer', { id: customerId, pageNumber: i }).then(() => {
                    const sites = this.sitesPerCustomers.map(item => ({
                        id: `${item.id}_site`,
                        name: item.name,
                        elementType: 'site',
                        pointsCount: item.points_count,
                        children: [],
                        customerIndex: this.localCustomers.indexOf(customer)
                    }));
                    if (sites.length > 0) {
                        if (customer.children) {
                            customer.children.push(...sites);
                        } else {
                            this.$set(customer, 'children', sites);
                        }
                        if (!sites.find(site => site.pointsCount > 0)) { // disable customer if it has no points
                            this.$set(customer, 'locked', true);
                        }
                        if (i === -1) {
                            break_loop = true;
                        }
                    } else {
                        break_loop = true;
                        if (i === -1) {
                            this.$set(customer, 'locked', true);
                            this.$delete(customer, 'children');
                        }
                    };
                });
                i += 1;
            }
        },
        async loadPoints (site) {
            let i = -1;
            site.loading = true;
            let break_loop = false;

            while (!break_loop) {
                const siteId = site.id.split('_')[0];
                await this.$store.dispatch('customers/getLocationsPerSite', { id: siteId, pageNumber: i }).then(() => {
                    site.loading = false;
                    const points = this.locationsPerSite.map(item => ({
                        id: `${item.id}_point`,
                        name: item.name,
                        elementType: STEP_ELEMENT_TYPE_ENUM.POINT
                    }));
                    if (points.length > 0) {
                        site.children.push(...points);
                        if (i === -1) {
                            break_loop = true;
                        }
                    } else {
                        break_loop = true;
                        if (i === -1) {
                            this.$set(site, 'locked', true);
                            this.$delete(site, 'children');
                        }
                    }
                });
                i += 1;
            }
        },
        removeSelected (item) {
            this.selected = this.selected.filter(i => i.id !== item.id);
        },
        async dispatchPointsActions (points) {
            for (const point of points) {
                if (point.elementType === STEP_ELEMENT_TYPE_ENUM.POINT) {
                    const pointId = point.id.split('_')[0];
                    await this.$store.dispatch('manageTrack/getStepInspectionTemplates', pointId);
                }
            }
            await this.$store.dispatch('cyclicRevisions/setSelectedTracksPoints', points);
        }
    }
};
</script>

<style scoped>
.treeview-item {
    max-width: 100px;
}
</style>
